import React, { useState, useEffect } from 'react'

// react router
import { useNavigate, useParams, } from "react-router-dom";

import { format, parse } from 'date-fns';

// axios
import axios from 'axios';
import { apiClient } from "api-client"

// redux
import { useDispatch, useSelector } from 'react-redux';
import { setReservationsById, setReservationForm } from 'redux/actions/reservationsActions';
import { setRefresh } from 'redux/actions/pageActions';
import { setOrderForm } from 'redux/actions/ordersActions';

// components
import LoadingComponent from 'components/LoadingComponent';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// components-lib
import ReservationInfo from "components-lib/ReservationInfo";
import FineInfo from "components-lib/FineInfo";
import EditReservation from 'components-lib/EditReservation';
import AddFine from 'components-lib/AddFine';

// notification
import { useSnackbar } from "notistack";

// language
import { injectIntl, IntlShape } from 'react-intl';

// types
import { Refresh, ReservationData, ReservationDataForm } from 'types/typesRedux';

// exracted data
import { extractDataReservation } from 'common/extractData';

// utils
import { formatDateTimeToIso } from 'utils';

// css
import '../../../css/mediaQuery.css'

type Props = {
  intl: IntlShape,
}

const ReservationDetail = ( { intl, }: Props) => {

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const { id } = useParams();

  // data from redux
  const refresh = useSelector((state: Refresh) => state.handleRefresh);
  const reservation = useSelector( (state: ReservationData) => state.reservationDetail.reservation );
  const reservationForm = useSelector( (state: ReservationDataForm) => state.reservationDetail.reservationForm );

  const [localLoading, setLocalLoading] = useState<boolean>(true);

  const [ reservationId, setReservationId ] = useState<number>(null);

  const [ isEdit, setIsEdit ] = useState<boolean>(false);
  const [ isAddFine, setIsAddFine ] = useState<boolean>(false);

  // Get reservation
  const getReservation = async ( id: number ) => {

    const cancelToken = axios.CancelToken.source()

    try {
      const response = await apiClient.get(`/v1/reservations/${id}/detail`,{
        cancelToken:cancelToken.token
      });
      setLocalLoading( false );

      if ( response ) {
        const responseData = extractDataReservation(intl)( response.data );
        dispatch(setReservationsById( responseData ));
        setLocalLoading( false );
      }
    } catch ( error ){
      console.log( 'err' )
    }
    return () => cancelToken.cancel()
  }

  // Handle edit reservation /////////////////////////////
  const editReservation = async ( key: string, data?: string ) => {
    const cancelToken = axios.CancelToken.source()

    let newDate;

    if ( key !== 'status') {
      newDate = formatDateTimeToIso( reservationForm?.date, reservationForm?.time );
    }

    const parsedDate = parse(reservation?.startAt, "dd.MM.yyyy HH:mm", new Date());
    const timeOnly = format(parsedDate, "HH:mm");
    const dateOnly = format(parsedDate, "dd.MM.yyyy");

    const params = (key === 'status')
      ? {status: data}
      : {startAt: newDate};

    if ( reservationForm.date === dateOnly && reservationForm.time === timeOnly ) {
      enqueueSnackbar(`${intl.formatMessage({id:'reservationEditAlert.nothingChanged'})}`, { variant: 'error' });
    }

    try {
      await apiClient.patch(`/v1/reservations/${reservation.id}`, params, {
        cancelToken:cancelToken.token
        }
      ).then( ( response ) => {
        if ( response ) {
          enqueueSnackbar(`${intl.formatMessage({id:'reservationEditAlert.success'})}`, { variant: 'success' });
          dispatch(setRefresh( { refresh: true } ));
          dispatch(setReservationForm( {
            status: '',
            issueFine: null,
            date: null,
            time: null,
          } 
        ));
        }
    });
    } catch ( err ){
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        enqueueSnackbar(`${intl.formatMessage({id:'reservationEditAlert.error'})}`, { variant: 'error' });
      }
    };
    return () => cancelToken.cancel();
  }

  const handleEditReservation = ( key: string, bool: boolean, data?: string,  ) => {
    if ( bool === true && key === 'status') {
      editReservation(key, data)
    } else if ( bool === true && key === 'newDateTime') {
      editReservation(key)
    }
  }

  // after open page reservation detail set id and get reservation data from api
  useEffect(() => {
    dispatch(setOrderForm( { status: '' } ));
    let reservationId = parseInt(id)
    setReservationId( reservationId )
    getReservation( reservationId )
  },[id]);

  useEffect(() => {
    if ( refresh.refresh === true ) {
      getReservation( reservationId );
      dispatch(setRefresh( { refresh: false } ))
    }
  }, [ refresh.refresh ])

  // handle close modal window
  const handleCloseWindow = ( type: string, bool: boolean ) => {
    if (bool === false && type === 'editReservation') {
      setIsEdit(false);
      dispatch(setReservationForm( {
        status: '',
        issueFine: null,
        date: null,
        time: null,
      }));
    } else if (bool === false && type === 'addFineReservation') {
      setIsAddFine(false);
    }
  }

  const handleAddFine = ( bool: boolean ) => {
    if ( bool === true ) setIsAddFine( bool );
  };

  // go to reservations table
  const handleHomePage = () => {
    navigate( `/admin/reservations`);
  };

  if ( localLoading ) {
    return <LoadingComponent />
  }

  return (
    <MDBox
      sx={{ marginLeft: "max(18.7rem)", paddingRight: "1rem", width: "auto",
      display: "flex", flexDirection: "column", justifyContent: "center", marginTop: "1rem"
      }}
    >
      <MDBox
        sx={{
          pb: "1rem", display: "flex", paddingRight: "1rem",
          direction: "row", gap: "1rem", width: "100%", 
          marginBottom: "1.3rem",
        }}
      >

        <MDBox width={7} xs={1.3}
          sx= {{
            display: "flex",    
            justifyContent: "flex-start",  
            width: "33%",                      
          }} >
          <MDButton 
            variant="outlined" 
            color="success" 
            onClick={handleHomePage}
            // onClick={ backUsersList }
          >
            {intl.formatMessage({id:'button.back'})}
          </MDButton>
        </MDBox>

      </MDBox>       
      
      <MDBox
        className= "containerReservations"
        sx={{
          width: "100%",
        }}
      >
        <MDBox 
          sx={{
            minWidth: "23rem", width: "auto", maxWidth: "28rem",
            pt: "1rem", pb: "1rem", pr: "1rem"
          }}
        >
          <ReservationInfo
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            handleEditReservation={handleEditReservation}
            handleAddFine={handleAddFine}
          />          
        </MDBox>

        <MDBox 
          sx={{
            minWidth: "23rem", width: "auto", maxWidth: "28rem",
            pt: "1rem", pb: "1rem", pr: "1rem"
          }}
        >
          { ( reservation.fineStatus || reservation.fineCreatedAt ) && (
            <FineInfo />
          )}
        </MDBox>        

        { isEdit && (
          <EditReservation
            onCloseWindow={ handleCloseWindow }
            onEditReservation={ handleEditReservation }
          />
        )}
        
        { isAddFine && (
          <AddFine
            type={ 'addFineReservation' }
            orderId={reservation.id}
            onCloseWindow={ handleCloseWindow }
          />
        )}

      </MDBox>
    </MDBox>
  )
}

export default injectIntl(ReservationDetail)